import { createSlice, createSelector } from '@reduxjs/toolkit';
import { CourierUser } from '@shared/sdk-v1';
import { RootState } from '../store';
import { hasSecret } from '../utils/courierSecrets';

interface AuthState {
  isAuthenticated: boolean;
  user?: CourierUser;
  token?: string;
}

const initialState: AuthState = {
  isAuthenticated: false,
  user: undefined,
  token: undefined,
};

// localStorage.setItem coerces non-strings to text, so a stored `undefined`
// becomes the literal string "undefined" — which is truthy but not valid JSON.
// Guard against that (and any other corrupt value) so init() never throws.
const safeParse = <T>(raw: string | null): T | undefined => {
  if (!raw || raw === 'undefined' || raw === 'null') return undefined;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return undefined;
  }
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    init: (state) => {
      const parsedToken = safeParse<{ id?: string }>(
        localStorage.getItem('accessToken')
      );
      const parsedUser = safeParse<CourierUser>(localStorage.getItem('user'));
      const tokenExpiration = localStorage.getItem('tokenExpiration');
      if (
        parsedToken?.id &&
        parsedUser &&
        tokenExpiration &&
        new Date(tokenExpiration) > new Date()
      ) {
        state.isAuthenticated = true;
        state.token = parsedToken.id;
        state.user = parsedUser;
      }
      // Otherwise stay logged out, but DON'T delete storage here: init() re-runs
      // right after login(), and a login response can legitimately lack `user`.
      // Wiping the still-valid token would make the next request unauthenticated
      // → 401 → forced logout. safeParse() already prevents crashes on bad data;
      // apiConfig() handles genuine token expiry + 401 cleanup.
    },
    login: (state, action) => {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('user');
      localStorage.removeItem('tokenExpiration');
      localStorage.removeItem('roles');
      state.isAuthenticated = true;
      state.token = action.payload.id; // AccessToken.id is the token string
      state.user = action.payload.user;
      localStorage.setItem('accessToken', JSON.stringify(action.payload));
      // Never persist an undefined user — JSON.stringify(undefined) is the JS
      // value undefined, which setItem coerces to the string "undefined" and
      // later breaks JSON.parse in init().
      if (action.payload.user) {
        localStorage.setItem('user', JSON.stringify(action.payload.user));
      } else {
        localStorage.removeItem('user');
      }
      // set the token expiration date by calculating the ttl + current time
      const tokenExpiration = new Date(
        Date.now() + (action.payload.ttl || 1209600) * 1000
      );
      localStorage.setItem('tokenExpiration', tokenExpiration.toISOString());
      // console.log(
      //   'Login successful, isAuthenticated set to:',
      //   state.isAuthenticated
      // );
    },
    logout: (state) => {
      state.isAuthenticated = false;
      state.token = undefined;
      state.user = undefined;
      localStorage.removeItem('accessToken');
      localStorage.removeItem('user');
      localStorage.removeItem('tokenExpiration');
      localStorage.removeItem('roles');
    },
    updateUser: (state, action) => {
      state.user = action.payload;
      localStorage.setItem('user', JSON.stringify(action.payload));
    },
    setUserRoles: (state, action) => {
      if (state.user) {
        state.user.roles = action.payload;
        localStorage.setItem('roles', JSON.stringify(action.payload));
      }
    },
  },
  selectors: {
    getIsAuthenticated: (state: AuthState) => state.isAuthenticated,
    getUser: (state: AuthState) => state.user,
    getUserId: (state: AuthState) => state.user?.id,
    getToken: (state: AuthState) => state.token,
  },
});

export const { init, login, logout, updateUser, setUserRoles } =
  authSlice.actions;

export const { getIsAuthenticated, getToken, getUserId } = authSlice.selectors;

// Memoized selectors that need to avoid creating new objects on every call
export const getUser = createSelector(
  [(state: RootState) => state.auth.user],
  (user) => user
);

// Custom selector that depends on courier settings
export const getHasApiKey = (state: RootState) => {
  const settings = state.courier.courier?.courierSetting;
  return !!(
    hasSecret(settings, 'jaShippersApiKey') ||
    hasSecret(settings, 'logisApiAccessToken')
  );
};

export default authSlice.reducer;
